import { X } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Diálogo com <dialog> nativo: foco preso, Esc fecha e o foco volta para quem abriu (docs/04 §4).
 * variant="drawer" desliza da direita (painéis de detalhe sem perder a lista — docs/04 §5.7).
 */
export function Dialog({ open, onClose, title, description, children, variant = 'modal', className }: {
  open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode;
  variant?: 'modal' | 'drawer'; className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={title}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
      className={cn(
        'bg-surface text-fg shadow-pop backdrop:bg-[oklch(0.2_0.02_255/0.45)] backdrop:backdrop-blur-[2px]',
        variant === 'drawer'
          ? 'fixed inset-y-0 right-0 left-auto m-0 h-dvh max-h-dvh w-full max-w-[520px] border-l border-border'
          : 'm-auto w-[min(92vw,520px)] rounded-xl border border-border',
        className,
      )}
    >
      {open && (
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-fg">{title}</h2>
              {description && <p className="mt-0.5 text-[13px] text-fg-muted">{description}</p>}
            </div>
            <button type="button" onClick={onClose} aria-label="Fechar"
              className="grid size-8 shrink-0 place-items-center rounded-md text-fg-subtle hover:bg-surface-muted hover:text-fg">
              <X className="size-4" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </div>
      )}
    </dialog>
  );
}
